import { useEffect, useRef, useState } from 'react'
import { getEmployee } from '../crud/employees.crud'

/**
 * Centralized hook for fetching fresh employee data when modals open from table
 *
 * This hook handles the common pattern of checking if employee data is stale (missing required fields)
 * and fetching fresh data when needed. It applies standard transformations for groups and licenses.
 *
 * @param {Object} options - Configuration options
 * @param {Object|Array} options.employee - Employee object or array of employees
 * @param {boolean} options.show - Whether the modal/form is shown
 * @param {Object} options.customer - Customer object (needs id property)
 * @param {Function} options.isStaleData - Function to check if employee data is stale (returns boolean)
 * @param {Function} options.transformData - Optional function to apply custom transformations to fetched data
 * @returns {Object|Array} - Fresh employee data (same shape as input)
 */
const useFreshEmployeeData = ({ employee, show, customer, isStaleData, transformData }) => {
  const [freshEmployee, setFreshEmployee] = useState(employee)
  const wasOpenRef = useRef(false)
  const lastFetchedEmployeeIdRef = useRef(null)
  const currentEmployeeIdRef = useRef(null)

  // Standard transformations for groups and licenses
  const applyStandardTransformations = (latestEmployee) => {
    if (!latestEmployee) return latestEmployee

    let transformed = { ...latestEmployee }

    // Transform groupsByType to expected frontend format
    if (transformed.groupsByType && !transformed.m365Groups) {
      transformed = {
        ...transformed,
        m365Groups: transformed.groupsByType.M365Group || [],
        securityGroups: transformed.groupsByType.SecurityGroup || [],
        distributionGroups: transformed.groupsByType.DistributionGroup || [],
        securityMailEnabledGroups: transformed.groupsByType.SecurityMailEnabledGroup || [],
        sharedMailboxes: transformed.groupsByType.SharedMailbox || [],
      }
    }

    // Normalize license data to licenseType format
    if (transformed.m365Licenses && Array.isArray(transformed.m365Licenses)) {
      transformed = {
        ...transformed,
        licenseType: transformed.m365Licenses
          .filter((license) => license != null && (license.licence_id || license.id))
          .map((license) => ({
            id: license.licence_id || license.id,
            displayName: license.product_display_name || license.licence_id || license.displayName || '',
          })),
      }
    } else if (!transformed.licenseType) {
      transformed = {
        ...transformed,
        licenseType: [],
      }
    }

    return transformed
  }

  useEffect(() => {
    const fetchFreshEmployee = async () => {
      if (!show) {
        wasOpenRef.current = false
        lastFetchedEmployeeIdRef.current = null
        currentEmployeeIdRef.current = null
        setFreshEmployee(employee)
        return
      }

      if (!employee) {
        setFreshEmployee(employee)
        wasOpenRef.current = show
        return
      }

      const isArray = Array.isArray(employee)

      if (!isArray) {
        const emp = employee
        const employeeIdentifier = emp?.employeeId ?? emp?.id
        currentEmployeeIdRef.current = employeeIdentifier
        const intendedId = employeeIdentifier
        const isModalJustOpened = !wasOpenRef.current && show
        const employeeIdChanged = lastFetchedEmployeeIdRef.current !== employeeIdentifier
        const isStale = isStaleData ? isStaleData(emp) : false
        const shouldFetch =
          employeeIdentifier != null && customer?.id && (isModalJustOpened || employeeIdChanged || isStale)

        if (shouldFetch) {
          try {
            const response = await getEmployee(employeeIdentifier)
            if (currentEmployeeIdRef.current !== intendedId) return
            let latestEmployee = response?.data

            latestEmployee = applyStandardTransformations(latestEmployee)

            if (transformData) {
              latestEmployee = transformData(latestEmployee)
            }

            setFreshEmployee({
              ...emp,
              ...latestEmployee,
            })
            lastFetchedEmployeeIdRef.current = employeeIdentifier
          } catch (error) {
            if (currentEmployeeIdRef.current !== intendedId) return
            console.error('Error fetching fresh employee data:', error)
            setFreshEmployee(employee)
          }
        } else {
          setFreshEmployee(employee)
          if (employeeIdentifier) {
            lastFetchedEmployeeIdRef.current = employeeIdentifier
          }
        }
      } else if (isArray && employee.length > 0) {
        const employeeIds = employee.map((emp) => emp?.employeeId ?? emp?.id).filter((id) => id != null)
        const intendedIdsStr = JSON.stringify(employeeIds.slice().sort())
        currentEmployeeIdRef.current = intendedIdsStr
        const isModalJustOpened = !wasOpenRef.current && show
        const lastIds = lastFetchedEmployeeIdRef.current || []
        const employeeIdsChanged = JSON.stringify(lastIds.sort()) !== JSON.stringify(employeeIds.sort())
        const hasStaleData = employee.some((emp) => (isStaleData ? isStaleData(emp) : false))
        const shouldFetch =
          employeeIds.length > 0 && customer?.id && (isModalJustOpened || employeeIdsChanged || hasStaleData)

        if (shouldFetch) {
          try {
            const fetchPromises = employee.map(async (emp) => {
              const employeeId = emp?.employeeId ?? emp?.id
              if (employeeId != null) {
                const response = await getEmployee(employeeId)
                let latestEmployee = response?.data

                latestEmployee = applyStandardTransformations(latestEmployee)

                if (transformData) {
                  latestEmployee = transformData(latestEmployee)
                }

                return {
                  ...emp,
                  ...latestEmployee,
                }
              }
              return emp
            })

            const fetchedEmployees = await Promise.all(fetchPromises)
            if (currentEmployeeIdRef.current !== intendedIdsStr) return
            setFreshEmployee(fetchedEmployees)
            lastFetchedEmployeeIdRef.current = employeeIds
          } catch (error) {
            if (currentEmployeeIdRef.current !== intendedIdsStr) return
            console.error('Error fetching fresh employee data:', error)
            setFreshEmployee(employee)
          }
        } else {
          setFreshEmployee(employee)
          if (employeeIds.length > 0) {
            lastFetchedEmployeeIdRef.current = employeeIds
          }
        }
      } else {
        setFreshEmployee(employee)
      }

      wasOpenRef.current = show
    }

    fetchFreshEmployee()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, employee, customer?.id])

  return freshEmployee
}

export default useFreshEmployeeData
